import React, { useEffect, useMemo, useState } from 'react';
import MaterialTable from 'material-table';
import moment from 'moment/min/moment-with-locales';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import { ThemeProvider } from '@mui/material/styles';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import { downloadCsv } from '../common/sharedFunctions';
import localization from './Table/Localization';
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2Select,
  C2SummaryCard,
  C2StatusChip,
  C2DocsChip,
} from './Table/C2UserTable';
import {
  c2DialogPaperSx,
  c2DialogTitleSx,
  c2DialogBodySx,
  c2IconBoxSx,
  c2SecondaryBtnSx,
  c2SuccessBtnSx,
  c2DangerBtnSx,
} from './conecta2ModalStyles';

const REJECT_REASONS = {
  invalid_amount: 'Monto inválido',
  insufficient_balance: 'Saldo insuficiente',
  below_minimum: 'Menor al mínimo',
  not_payday: 'Fuera de día de pago',
  payday_withdraw_cap: 'Supera el cupo del día',
  user_not_found: 'Usuario no encontrado',
  admin: 'Rechazado por admin',
};

const rejectLabel = (r) => {
  const base = REJECT_REASONS[r.rejectReason] || r.rejectReason || '';
  return r.rejectReason === 'admin' && r.rejectMessage ? `${base}: ${r.rejectMessage}` : base;
};

const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const formatDateTime = (ms) =>
  ms ? `${new Date(ms).toLocaleDateString()} ${new Date(ms).toLocaleTimeString()}` : '';

const statusOf = (w) => (w.rejected ? 'rejected' : w.processed ? 'processed' : 'pending');

function DetailRow({ label, children }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.75, borderBottom: `1px solid ${C2.lavender}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.muted }}>{label}</Typography>
      <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.88rem', fontWeight: 700, color: C2.sidebar, textAlign: 'right', wordBreak: 'break-word' }}>
        {children || '—'}
      </Typography>
    </Box>
  );
}

/**
 * Tabla de solicitudes de retiro con resumen, filtro por estado y confirmación de pago.
 * `onMarkPaid(row)` debe devolver una promesa.
 */
export default function C2WithdrawsPanel({
  rows,
  settings,
  title,
  subtitle,
  emptyMessage,
  csvFileName,
  formatAmount,
  onMarkPaid,
  onReject,
  getUserContext,
  requireValidation = false,
  showFee = false,
  processedCaption,
  errorMsg,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const [selectedRow, setSelectedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [confirmRow, setConfirmRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [rejectMessage, setRejectMessage] = useState('');
  const [userContext, setUserContext] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [notice, setNotice] = useState(null);
  const [dismissedError, setDismissedError] = useState(null);

  const dialogRow = confirmRow || rejectRow;
  useEffect(() => {
    setUserContext(null);
    if (!dialogRow || !getUserContext) return undefined;
    let active = true;
    Promise.resolve(getUserContext(dialogRow.uid))
      .then((ctx) => {
        if (active) setUserContext(ctx || { missing: true });
      })
      .catch(() => {
        if (active) setUserContext({ missing: true });
      });
    return () => {
      active = false;
    };
  }, [dialogRow, getUserContext]);

  const isUnvalidated = (w) => requireValidation && !w.validated;
  const isValidating = (w) => isUnvalidated(w) && w.date && Date.now() - Number(w.date) < 2 * 60 * 1000;

  const showBank = settings.bank_fields !== false;
  const data = useMemo(() => rows || [], [rows]);

  const summary = useMemo(() => {
    const s = { pendingCount: 0, pendingTotal: 0, processedCount: 0, processedTotal: 0, rejectedCount: 0, feesTotal: 0 };
    data.forEach((w) => {
      const hasFee = showFee && w.netAmount != null;
      const amount = hasFee ? Number(w.netAmount) || 0 : parseFloat(w.amount) || 0;
      const st = statusOf(w);
      if (st === 'processed') {
        s.processedCount += 1;
        s.processedTotal += amount;
        if (hasFee) s.feesTotal += Number(w.feeAmount) || 0;
      } else if (st === 'pending') {
        s.pendingCount += 1;
        s.pendingTotal += amount;
      } else {
        s.rejectedCount += 1;
      }
    });
    return s;
  }, [data, showFee]);

  const filteredData = useMemo(
    () => (statusFilter ? data.filter((w) => statusOf(w) === statusFilter) : data),
    [data, statusFilter]
  );

  const handleConfirm = () => {
    if (!confirmRow || isUnvalidated(confirmRow)) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setConfirmRow(null);
      setNotice({ severity: 'warning', msg: t('demo_mode') });
      return;
    }
    setProcessing(true);
    Promise.resolve(onMarkPaid(confirmRow))
      .then(() => {
        setNotice({ severity: 'success', msg: `Retiro de ${confirmRow.name || 'usuario'} marcado como pagado.` });
      })
      .catch(() => {
        setNotice({ severity: 'error', msg: 'No se pudo marcar el retiro como pagado. Intenta de nuevo.' });
      })
      .finally(() => {
        setProcessing(false);
        setConfirmRow(null);
      });
  };

  const handleReject = () => {
    if (!rejectRow || !onReject || !rejectMessage.trim()) return;
    if (!settings.AllowCriticalEditsAdmin) {
      setRejectRow(null);
      setNotice({ severity: 'warning', msg: t('demo_mode') });
      return;
    }
    const row = rejectRow;
    setProcessing(true);
    Promise.resolve(onReject(row, rejectMessage.trim()))
      .then(() => {
        setNotice({
          severity: 'success',
          msg: row.validated
            ? `Retiro de ${row.name || 'usuario'} rechazado. El saldo se devolverá a su bono de red.`
            : `Retiro de ${row.name || 'usuario'} rechazado.`,
        });
      })
      .catch(() => {
        setNotice({ severity: 'error', msg: 'No se pudo rechazar el retiro. Intenta de nuevo.' });
      })
      .finally(() => {
        setProcessing(false);
        setRejectRow(null);
        setRejectMessage('');
      });
  };

  const contextRows = userContext && !userContext.missing ? (
    <Box sx={{ mt: 1.5, p: 1.5, borderRadius: '12px', backgroundColor: C2.bg, border: `1px solid ${C2.border}` }}>
      <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: C2.violet, mb: 0.5 }}>
        Estado actual del usuario
      </Typography>
      {(userContext.rows || []).map((r) => (
        <DetailRow key={r.label} label={r.label}>{r.value}</DetailRow>
      ))}
    </Box>
  ) : userContext && userContext.missing ? (
    <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}>
      No se encontró el perfil del usuario.
    </Alert>
  ) : null;

  const columns = [
    {
      title: t('requestDate'),
      field: 'date',
      defaultSort: 'desc',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.sidebar }}>
            {r.date ? moment(r.date).format('lll') : '—'}
          </Typography>
          {r.payday ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Pago del día {r.payday}</Typography>
          ) : null}
        </Box>
      ),
      exportTransformer: (r) => formatDateTime(r.date),
    },
    {
      title: t('driver_name'),
      field: 'name',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.88rem', color: C2.sidebar }}>
            {r.name || '—'}
          </Typography>
          {r.type === 'referral_bonus' ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted }}>Bono de red</Typography>
          ) : null}
        </Box>
      ),
    },
    {
      title: t('amount'),
      field: 'amount',
      render: (r) => (
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '0.95rem', color: C2.sidebar, whiteSpace: 'nowrap' }}>
            {formatAmount(r.amount)}
          </Typography>
          {showFee && r.netAmount != null ? (
            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.74rem', color: C2.muted, whiteSpace: 'nowrap' }}>
              Transferir {formatAmount(r.netAmount)} · fee {r.feePercent}%
            </Typography>
          ) : null}
        </Box>
      ),
    },
    { title: 'Fee plataforma', field: 'feeAmount', hidden: true, export: showFee },
    { title: 'Neto a transferir', field: 'netAmount', hidden: true, export: showFee },
    {
      title: t('processed'),
      field: 'processed',
      render: (r) => {
        const st = statusOf(r);
        if (st === 'rejected') {
          return (
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <C2StatusChip tone="danger" label="Rechazado" />
              {r.rejectReason ? (
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.72rem', color: C2.muted, maxWidth: 220 }}>
                  {rejectLabel(r)}
                </Typography>
              ) : null}
            </Box>
          );
        }
        return st === 'processed' ? (
          <C2StatusChip tone="success" label="Pagado" />
        ) : (
          <C2StatusChip tone="warning" label="Pendiente" />
        );
      },
      exportTransformer: (r) => {
        const st = statusOf(r);
        if (st === 'rejected') return `Rechazado (${rejectLabel(r)})`;
        return st === 'processed' ? 'Sí' : 'No';
      },
    },
    {
      title: 'Validación',
      field: 'validated',
      hidden: !requireValidation,
      export: requireValidation,
      render: (r) => {
        if (statusOf(r) === 'rejected') return '—';
        if (r.validated) return <C2StatusChip tone="success" label="Validado" dot={false} />;
        return isValidating(r)
          ? <C2StatusChip tone="violet" label="Validando…" dot={false} />
          : <C2StatusChip tone="danger" label="Sin validar" dot={false} />;
      },
      exportTransformer: (r) => (r.validated ? 'Sí' : 'No'),
    },
    {
      title: t('processDate'),
      field: 'processDate',
      render: (r) => (r.processDate ? moment(r.processDate).format('lll') : '—'),
      exportTransformer: (r) => formatDateTime(r.processDate),
    },
    {
      title: 'Cuenta bancaria',
      field: 'bankSummary',
      hidden: !showBank,
      export: false,
      sorting: false,
      render: (r) =>
        r.bankName || r.bankAccount ? (
          <Box sx={{ textAlign: 'left' }}>
            <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.84rem', color: C2.sidebar }}>
              {r.bankName || '—'}
              {r.bankCode ? ` · ${r.bankCode}` : ''}
            </Typography>
            <Typography sx={{ fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: '0.78rem', color: C2.muted }}>
              {r.bankAccount || '—'}
            </Typography>
          </Box>
        ) : (
          <C2StatusChip tone="danger" label="Sin datos bancarios" dot={false} />
        ),
    },
    { title: t('bankName'), field: 'bankName', hidden: true, export: showBank },
    { title: t('bankCode'), field: 'bankCode', hidden: true, export: showBank },
    { title: t('bankAccount'), field: 'bankAccount', hidden: true, export: showBank },
    { title: 'ID', field: 'id', hidden: true, export: true },
  ];

  const exportCsv = (cols, exportRows) => {
    const headerRow = cols.map((col) => (typeof col.title === 'object' ? col.title.props.text : col.title));
    const dataRows = exportRows.map(({ tableData, ...row }) =>
      cols.map((col) => (col.exportTransformer ? col.exportTransformer(row) : row[col.field]))
    );
    const csvContent = [headerRow, ...dataRows].map((r) => r.map(csvCell).join(',')).join('\n');
    downloadCsv(csvContent, csvFileName);
  };

  const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'pending', label: 'Pendientes' },
    { value: 'processed', label: 'Pagados' },
  ];
  if (summary.rejectedCount > 0) statusOptions.push({ value: 'rejected', label: 'Rechazados' });

  const tableComponents = c2Toolbar(
    <C2Select
      id="withdraw-status-filter"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={170}
      options={statusOptions}
    />
  );

  const emptyTitle = {
    pending: 'No hay retiros pendientes',
    processed: 'Aún no hay retiros pagados',
    rejected: 'No hay retiros rechazados',
  }[statusFilter] || 'Aún no hay solicitudes de retiro';

  const visibleError = errorMsg && errorMsg !== dismissedError ? errorMsg : null;

  return (
    <Box sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <ThemeProvider theme={c2TableTheme}>
        {visibleError ? (
          <Alert severity="error" onClose={() => setDismissedError(errorMsg)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {visibleError}
          </Alert>
        ) : null}
        {notice ? (
          <Alert severity={notice.severity} onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily: C2_FONT }}>
            {notice.msg}
          </Alert>
        ) : null}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={PendingActionsOutlinedIcon}
              label="Pendientes por pagar"
              value={summary.pendingCount}
              caption={summary.pendingCount === 1 ? 'solicitud' : 'solicitudes'}
              accent={summary.pendingCount > 0 ? '#D97706' : '#16A34A'}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={PaymentsOutlinedIcon}
              label="Monto pendiente"
              value={formatAmount(summary.pendingTotal)}
              caption={showFee ? 'Neto por transferir (ya sin fee)' : 'Por transferir a los beneficiarios'}
              accent={summary.pendingTotal > 0 ? '#D97706' : C2.violet}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={TaskAltOutlinedIcon}
              label="Retiros pagados"
              value={summary.processedCount}
              caption={processedCaption}
              accent="#16A34A"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <C2SummaryCard
              icon={AccountBalanceOutlinedIcon}
              label="Total pagado"
              value={formatAmount(summary.processedTotal)}
              caption={[
                showFee && summary.feesTotal > 0 ? `Fees cobrados ${formatAmount(summary.feesTotal)}` : null,
                summary.rejectedCount ? `${summary.rejectedCount} rechazados` : null,
              ].filter(Boolean).join(' · ') || 'En los registros mostrados'}
            />
          </Grid>
        </Grid>

        <C2TableCard>
          <C2SectionHeading title={title} subtitle={subtitle} />
          <MaterialTable
            title={title}
            columns={columns}
            data={filteredData}
            style={c2TableStyle(isRTL)}
            components={tableComponents}
            onRowClick={(evt, row) => setSelectedRow(row.tableData.id)}
            options={{
              ...c2TableOptions(selectedRow),
              pageSize: 10,
              pageSizeOptions: [10, 15, 20],
              exportCsv,
              exportButton: {
                csv: settings.AllowCriticalEditsAdmin,
                pdf: false,
              },
            }}
            localization={c2Localization(
              localization(t),
              <C2EmptyState icon={PaymentsOutlinedIcon} title={emptyTitle} message={emptyMessage} />
            )}
            actions={[
              (rowData) =>
                statusOf(rowData) === 'pending'
                  ? {
                      icon: () => <C2DocsChip icon={CheckCircleOutlineRoundedIcon} label="Marcar pagado" />,
                      tooltip: t('process_withdraw'),
                      onClick: (event, row) => setConfirmRow(row),
                    }
                  : {
                      icon: () => (
                        <C2StatusChip
                          tone="neutral"
                          label={statusOf(rowData) === 'rejected' ? 'Rechazado' : 'Pagado'}
                          dot={false}
                        />
                      ),
                      tooltip: statusOf(rowData) === 'rejected' ? 'Rechazado' : t('processed'),
                      disabled: true,
                      onClick: () => {},
                    },
              (rowData) => ({
                icon: () => <C2DocsChip icon={BlockRoundedIcon} label="Rechazar" />,
                tooltip: 'Rechazar retiro',
                hidden: !onReject || statusOf(rowData) !== 'pending' || isValidating(rowData),
                onClick: (event, row) => {
                  setRejectMessage('');
                  setRejectRow(row);
                },
              }),
            ]}
          />
        </C2TableCard>

        <Dialog
          open={!!confirmRow}
          onClose={() => (processing ? null : setConfirmRow(null))}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: c2DialogPaperSx }}
        >
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ ...c2IconBoxSx, background: 'linear-gradient(135deg, #22C55E, #16A34A)' }}>
                <CheckCircleOutlineRoundedIcon sx={{ color: '#fff' }} />
              </Box>
              <Typography sx={c2DialogTitleSx}>Confirmar pago del retiro</Typography>
            </Box>
            <Typography sx={{ ...c2DialogBodySx, mb: 1.5 }}>
              Confirma que ya transferiste este monto. Esta acción no se puede deshacer.
            </Typography>
            {confirmRow ? (
              <Box>
                <DetailRow label="Beneficiario">{confirmRow.name}</DetailRow>
                {showFee && confirmRow.netAmount != null ? (
                  <>
                    <DetailRow label="Monto solicitado">{formatAmount(confirmRow.amount)}</DetailRow>
                    <DetailRow label={`Fee de plataforma (${confirmRow.feePercent}%)`}>{`− ${formatAmount(confirmRow.feeAmount)}`}</DetailRow>
                    <DetailRow label="A transferir">
                      <Box component="span" sx={{ color: '#16A34A', fontSize: '1rem' }}>{formatAmount(confirmRow.netAmount)}</Box>
                    </DetailRow>
                  </>
                ) : (
                  <DetailRow label="Monto">{formatAmount(confirmRow.amount)}</DetailRow>
                )}
                <DetailRow label="Solicitado">{confirmRow.date ? moment(confirmRow.date).format('lll') : null}</DetailRow>
                {showBank ? (
                  <>
                    <DetailRow label={t('bankName')}>{confirmRow.bankName}</DetailRow>
                    <DetailRow label={t('bankCode')}>{confirmRow.bankCode}</DetailRow>
                    <DetailRow label={t('bankAccount')}>{confirmRow.bankAccount}</DetailRow>
                  </>
                ) : null}
                {contextRows}
                {isUnvalidated(confirmRow) ? (
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: '12px', fontFamily: C2_FONT }}>
                    El sistema no validó esta solicitud: no hay garantía de que el saldo se haya descontado. No se puede pagar; recházala.
                  </Alert>
                ) : null}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button variant="outlined" onClick={() => setConfirmRow(null)} disabled={processing} sx={c2SecondaryBtnSx}>
              {t('cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirm}
              disabled={processing || (confirmRow ? isUnvalidated(confirmRow) : false)}
              sx={c2SuccessBtnSx}
            >
              {processing ? 'Guardando…' : 'Sí, marcar pagado'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!rejectRow}
          onClose={() => (processing ? null : setRejectRow(null))}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: c2DialogPaperSx }}
        >
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box sx={{ ...c2IconBoxSx, background: 'linear-gradient(135deg, #F87171, #DC2626)' }}>
                <BlockRoundedIcon sx={{ color: '#fff' }} />
              </Box>
              <Typography sx={c2DialogTitleSx}>Rechazar retiro</Typography>
            </Box>
            {rejectRow ? (
              <Box>
                <Typography sx={{ ...c2DialogBodySx, mb: 1.5 }}>
                  {rejectRow.validated
                    ? `Se devolverán ${formatAmount(rejectRow.amount)} al bono de red del conductor y se le notificará el motivo.`
                    : 'Esta solicitud no fue validada por el sistema, así que no se devuelve saldo. Se le notificará el motivo.'}
                </Typography>
                <DetailRow label="Beneficiario">{rejectRow.name}</DetailRow>
                <DetailRow label="Monto">{formatAmount(rejectRow.amount)}</DetailRow>
                <DetailRow label="Solicitado">{rejectRow.date ? moment(rejectRow.date).format('lll') : null}</DetailRow>
                {contextRows}
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Motivo del rechazo"
                  value={rejectMessage}
                  onChange={(e) => setRejectMessage(e.target.value.slice(0, 300))}
                  helperText="Obligatorio. El conductor lo verá en la notificación."
                  sx={{
                    mt: 2,
                    '& .MuiOutlinedInput-root': { borderRadius: '12px', fontFamily: C2_FONT },
                    '& .MuiInputLabel-root': { fontFamily: C2_FONT },
                    '& .MuiFormHelperText-root': { fontFamily: C2_FONT },
                  }}
                />
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button variant="outlined" onClick={() => setRejectRow(null)} disabled={processing} sx={c2SecondaryBtnSx}>
              {t('cancel')}
            </Button>
            <Button variant="outlined" onClick={handleReject} disabled={processing || !rejectMessage.trim()} sx={c2DangerBtnSx}>
              {processing ? 'Guardando…' : 'Sí, rechazar'}
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    </Box>
  );
}
